// Feature 009: Deal Operating System
// Document drop zone overlay for file upload
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useCallback, useEffect, useRef } from 'react';

const DocumentDropZone = ({ onFileDrop, isActive, onDragStateChange }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  // Counter technique: dragenter increments, dragleave decrements
  // This handles child element enter/leave events correctly
  const dragCounterRef = useRef(0);

  // Reset state when zone becomes inactive
  useEffect(() => {
    if (!isActive) {
      setIsDragOver(false);
      dragCounterRef.current = 0;
    }
  }, [isActive]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;

    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Required to allow drop
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;

    // Only set false when all elements have been left
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    onDragStateChange?.(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpeg',
        'text/plain',
      ];
      return validTypes.includes(file.type) || file.name.match(/\.(pdf|pptx|docx|png|jpg|jpeg|txt)$/i);
    });

    if (validFiles.length > 0) {
      onFileDrop(validFiles);
    }
  }, [onFileDrop, onDragStateChange]);

  // Global drag listeners to detect when files enter/leave the window
  useEffect(() => {
    let windowDragCounter = 0;

    const handleWindowDragEnter = (e) => {
      e.preventDefault();
      windowDragCounter++;

      // Show drop zone when files are dragged into the window
      if (windowDragCounter === 1 && e.dataTransfer.types.includes('Files')) {
        onDragStateChange?.(true);
      }
    };

    const handleWindowDragOver = (e) => {
      // Prevent default to allow drop
      e.preventDefault();
      if (e.dataTransfer.types.includes('Files')) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleWindowDragLeave = (e) => {
      e.preventDefault();
      windowDragCounter--;

      // Hide drop zone when files leave the window
      if (windowDragCounter === 0) {
        onDragStateChange?.(false);
      }
    };

    const handleWindowDrop = (e) => {
      // Prevent browser from opening the file
      e.preventDefault();
      windowDragCounter = 0;

      // If dropped outside the drop zone, just hide it
      if (!e.target.closest('[data-testid="file-drop-zone"]')) {
        onDragStateChange?.(false);
      }
    };

    // Use capture phase to ensure we get all events
    document.addEventListener('dragenter', handleWindowDragEnter, true);
    document.addEventListener('dragover', handleWindowDragOver, true);
    document.addEventListener('dragleave', handleWindowDragLeave, true);
    document.addEventListener('drop', handleWindowDrop, true);

    return () => {
      document.removeEventListener('dragenter', handleWindowDragEnter, true);
      document.removeEventListener('dragover', handleWindowDragOver, true);
      document.removeEventListener('dragleave', handleWindowDragLeave, true);
      document.removeEventListener('drop', handleWindowDrop, true);
    };
  }, [onDragStateChange]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isActive) {
        dragCounterRef.current = 0;
        setIsDragOver(false);
        onDragStateChange?.(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, onDragStateChange]);

  if (!isActive) return null;

  return (
    <div
      data-testid="file-drop-zone"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`fixed inset-0 z-50 transition-all duration-200 ${
        isDragOver ? 'bg-black/95' : 'bg-black/90'
      }`}
      style={{ pointerEvents: 'all' }}
    >
      {/* Invisible full-coverage drop target */}
      <div
        className="absolute inset-0"
        style={{ pointerEvents: 'all' }}
      />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className={`border-2 border-dashed p-16 text-center transition-all duration-200 ${
            isDragOver
              ? 'border-[#a1c6a2] bg-[rgba(161,198,162,0.1)] scale-105'
              : 'border-[rgba(255,251,247,0.3)]'
          }`}
        >
          {/* Upload Icon */}
          <svg
            className={`w-20 h-20 mx-auto mb-6 transition-colors ${
              isDragOver ? 'text-[#a1c6a2]' : 'text-[rgba(255,251,247,0.5)]'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>

          <h2
            className="text-2xl font-bold text-[#fffbf7] mb-3"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            {isDragOver ? 'Release to Upload' : 'Drop Document to Create Opportunity'}
          </h2>

          <p className="text-sm text-[rgba(255,251,247,0.5)] mb-6">
            Drop a document and our AI will extract deal information automatically
          </p>

          {/* Supported formats */}
          <div className="flex items-center justify-center gap-4">
            {['PDF', 'PPTX', 'DOCX', 'PNG', 'TXT'].map((format) => (
              <span
                key={format}
                className="px-3 py-1 text-[10px] tracking-wider uppercase bg-[rgba(255,251,247,0.1)] text-[rgba(255,251,247,0.6)]"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                {format}
              </span>
            ))}
          </div>

          {/* Size limit notice */}
          <p
            className="mt-6 text-[10px] text-[rgba(255,251,247,0.3)] tracking-wider"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Maximum file size: 200MB
          </p>
        </div>
      </div>

      {/* Close hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 pointer-events-none">
        <span
          className="text-xs text-[rgba(255,251,247,0.3)] tracking-wider"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          Press ESC to cancel
        </span>
      </div>
    </div>
  );
};

export default DocumentDropZone;
